<script setup>
import { ref, onMounted, onUnmounted, computed, watch, nextTick } from "vue";
import { motion } from "motion-v";
import { AnimatePresence } from "motion-v";
import storage from "@/composables/common/storage";
import { useDevice } from "@/composables/useDevice";
import { useChat } from "@/composables/useSidebar";
import { useAuth } from "@/composables/useAuth";
import { useSocket } from "@/composables/useSocket";
import { useNotifications } from "@/composables/useNotifications";
import {
  useFormField,
  usePasswordField,
  usePasswordConfirmField,
  useFormValidation,
  validateEmail,
} from "~/composables/useForm";

// ===== 상수 =====
const THEME_STORAGE_KEY = "app-theme";
const NOTIFICATION_MAX_DISPLAY = 99;

// ===== 더미 데이터 =====
const TEST_DATA = {
  // jackpot: 1,
  chat: 12,
  notification: 100,
};

// ===== Composables =====
const { isMobile } = useDevice();
const {
  isChatOpen,
  toggleChat,
  unreadMessageCount,
  isNotificationOpen,
  toggleNotification,
} = useChat();
const { fetchUnreadCount } = useNotifications();
const {
  isLoggedIn,
  currentUser,
  logout,
  login,
  initialize: initializeAuth,
  isInitialized: isAuthInitialized,
  updateBalance,
} = useAuth();

// 랜덤 기본 아바타 (01~06)
const getDefaultAvatar = () => {
  const num = Math.floor(Math.random() * 6) + 1;
  return `/images/default_avatar_0${num}.png`;
};

// 아바타 이미지
const avatarUrl = computed(() => {
  if (!currentUser.value?.avatar) {
    return getDefaultAvatar();
  }

  const avatar = currentUser.value.avatar;

  // Base64 이미지인 경우 그대로 반환
  if (avatar.startsWith('data:image')) {
    return avatar;
  }

  // 숫자만 있는 경우 (예: "03") 경로 조합
  if (/^\d{2}$/.test(avatar)) {
    return `/images/default_avatar_${avatar}.png`;
  }

  // 이미 경로인 경우 그대로 반환
  if (avatar.startsWith('/images/')) {
    return avatar;
  }

  // 기타 경우 기본 아바타
  return getDefaultAvatar();
});

// 아바타 이미지 에러 처리
const handleAvatarError = (event) => {
  event.target.src = getDefaultAvatar();
};

// ===== Refs =====
// 마운트 상태 (hydration mismatch 방지용)
const isMounted = ref(false);

// 모달 refs
// const jackpotModal = ref(null);
// const jackpotHistoryModal = ref(null);
// const jackpotBonusModal = ref(null);
// const jackpotRankStandardModal = ref(null);
const loginModal = ref(null);
const signupModal = ref(null);
const passwordRequestModal = ref(null);

// 알림 관련 상태
const unreadChatCount = unreadMessageCount;
const { unreadNotificationCount } = useChat();
// const unreadJackpotCount = ref(0);

// 잭팟 관련 상태
// const jackpotRank = ref(null);

// 더미 데이터
// const jackpotHistoryMeta = ref({
//   round: 1,
//   startDate: "2025.12.05 (금)",
//   startTime: "00:00:00",
//   endDate: "2025.12.12 (목) 23:59:59",
// });

// 테마 상태
const currentTheme = ref("dark");

// 스크롤 상태
const isScrolled = ref(false);
const mainElementRef = ref(null);

// 유저 메뉴 상태
const isUserMenuOpen = ref(false);

// 언어 서브메뉴 상태
const isLangSubmenuOpen = ref(false);

// 사운드 상태 (localStorage에서 불러오기)
// const SOUND_STORAGE_KEY = "app-sound";
// const isSoundOn = ref(true);

// 비로그인용 언어 메뉴 상태
const isLangMenuOpen = ref(false);

// ===== 로그인 폼 필드 =====
// 계정 필드 (이메일 형식 검증 제거)
const email = useFormField("", {
  required: true,
  errorMessages: {
    empty: "계정을 입력해주세요.",
    invalid: "계정을 확인해주세요.",
  },
});

// 비밀번호 필드 (영문숫자 8자 검증 제거)
const password = useFormField("", {
  required: true,
  errorMessages: {
    empty: "비밀번호를 입력해주세요.",
    mismatch: "계정 또는 비밀번호가 일치하지 않습니다.",
  },
});

// 비밀번호 표시/숨김 상태
const showPassword = ref(false);
const togglePassword = () => {
  showPassword.value = !showPassword.value;
};

// ===== 로그인 폼 관리 =====
const form = useFormValidation(
  { email, password },
  {
    onSubmit: async (values) => {
      // 로그인 시도
      const result = await login(values.email, values.password);

      if (result.success) {
        // 로그인 성공 시 모달 닫기
        loginModal.value?.close();
        // 폼 초기화
        email.value.value = "";
        password.value.value = "";
        return true;
      } else {
        // 로그인 실패 시 에러 표시
        password.reset();
        await nextTick();
        password.setError("mismatch");
        await nextTick();
        return false;
      }
    },
  }
);

// ===== 로그인 함수 =====
const handleLogin = async () => {
  await form.handleSubmit();
};

// 로그인 모달 열기
const openLoginModal = () => {
  loginModal.value?.open();
};

// 로그인 모달 다시 열기 (비밀번호 찾기 모달 닫을 때 사용)
const reopenLoginModal = () => {
  loginModal.value?.open();
};

// 로그인 모달이 열려 있으면 닫기 (비밀번호 찾기 모달 열 때 사용)
const closeLoginModalIfOpen = () => {
  if (loginModal.value?.isOpened()) {
    loginModal.value?.close();
  }
};

// 로그인 모달 닫기 시 폼 초기화
const handleLoginModalClose = () => {
  // 에러 상태 초기화
  email.reset();
  password.reset();
  // 입력값 초기화
  email.value.value = "";
  password.value.value = "";
};

// ===== 비밀번호 찾기 (요청) 폼 =====
const isPasswordRequestSubmitted = ref(false);

const requestPasswordEmail = useFormField("", {
  required: true,
  validator: validateEmail,
  errorMessages: {
    empty: "이메일을 입력해주세요.",
    invalid: "이메일 형식이 올바르지 않습니다.",
  },
});

const requestPasswordForm = useFormValidation(
  { email: requestPasswordEmail },
  {
    onSubmit: async (values) => {
      isPasswordRequestSubmitted.value = true;
      return true;
    },
  }
);

const isPasswordRequestFormValid = computed(
  () =>
    requestPasswordEmail.value.value &&
    !requestPasswordEmail.isError.value
);

const handlePasswordRequest = async () => {
  await requestPasswordForm.handleSubmit();
};

const openPasswordRequestModal = () => {
  closeLoginModalIfOpen();
  passwordRequestModal.value?.open();
};

const handlePasswordRequestModalClose = () => {
  requestPasswordEmail.reset();
  requestPasswordEmail.value.value = "";
  isPasswordRequestSubmitted.value = false;
};

// 비밀번호 찾기 완료 후 로그인 모달 열기 (닫으면 previous-modal로 로그인 모달이 열림)
const handlePasswordRequestDoneLogin = () => {
  passwordRequestModal.value?.close();
};

// ===== 회원가입 폼 필드 =====
// 이메일 필드
const signupEmail = useFormField("", {
  required: true,
  validator: validateEmail,
  errorMessages: {
    empty: "이메일을 입력해주세요.",
    invalid: "이메일 형식이 올바르지 않습니다.",
    duplicate: "이미 가입된 이메일입니다.",
    "auth-failed": "인증 오류가 발생했습니다.",
  },
  onAuth: async (emailValue) => {
    // TODO: 실제 이메일 인증 API 호출
    // console.log("Email auth:", emailValue);

    // 테스트용: 무조건 인증 실패
    return { success: false, errorCode: "auth-failed" };
  },
});

// 닉네임 필드
const signupNickname = useFormField("", {
  required: true,
  errorMessages: {
    empty: "닉네임을 입력해주세요.",
    duplicate: "중복된 닉네임입니다.",
  },
});

// 비밀번호 필드
const signupPassword = usePasswordField("", {
  required: true,
  errorMessages: {
    empty: "비밀번호를 입력해주세요.",
    invalid: "비밀번호는 영문숫자 포함 8자 이상이어야 합니다.",
  },
});

// 비밀번호 확인 필드
const signupPasswordConfirm = usePasswordConfirmField("", signupPassword, {
  errorMessages: {
    empty: "비밀번호를 입력해주세요.",
    mismatch: "비밀번호가 일치하지 않습니다.",
  },
});

// 추천인 코드 필드
const signupReferralCode = useFormField("", {
  required: true,
  errorMessages: {
    empty: "추천인 코드를 입력해 주세요.",
    invalid: "잘못된 추천인 코드입니다. 다시 확인해 주세요.",
    expired: "이미 만료된 추천인 코드입니다.",
    self: "본인의 코드는 추천인으로 등록할 수 없습니다.",
    "server-error": "코드 확인 중 오류가 발생했습니다.",
  },
});

// 사용자 계약 동의 여부
const agreeToTerms = ref(false);

// ===== 회원가입 폼 관리 =====
const signupForm = useFormValidation(
  {
    email: signupEmail,
    nickname: signupNickname,
    password: signupPassword,
    passwordConfirm: signupPasswordConfirm,
    referralCode: signupReferralCode,
  },
  {
    additionalValidations: [
      {
        condition: agreeToTerms.value,
        field: null,
        errorCode: "terms-required",
      },
    ],
    onSubmit: async (values) => {
      // TODO: 실제 회원가입 API 호출
      // console.log("Signup attempt:", values);
      // 테스트용: 이메일 중복, 추천인 코드 에러 표시
      signupEmail.setError("duplicate");
      signupReferralCode.setError("invalid");
      return false;
    },
  }
);

// 폼 유효성 검사 (사용자 계약 동의 포함)
const isSignupFormValid = computed(() => {
  return signupForm.isFormValid.value && agreeToTerms.value;
});

// ===== 회원가입 함수 =====
// 이메일 인증 버튼 클릭 핸들러
const handleEmailAuth = async () => {
  await signupEmail.handleAuth();
};

// 가입 버튼 클릭 핸들러
const handleSignup = async () => {
  await signupForm.handleSubmit();
};

// 회원가입 모달 열기
const openSignupModal = () => {
  signupModal.value?.open();
};

// 회원가입 모달 닫기 시 폼 초기화
const handleSignupModalClose = () => {
  // 에러 상태 초기화
  signupEmail.reset();
  signupNickname.reset();
  signupPassword.reset();
  signupPasswordConfirm.reset();
  signupReferralCode.reset();
  // 입력값 초기화
  signupEmail.value.value = "";
  signupNickname.value.value = "";
  signupPassword.value.value = "";
  signupPasswordConfirm.value.value = "";
  signupReferralCode.value.value = "";
  // 동의 체크박스 초기화
  agreeToTerms.value = false;
};


// ===== Google Translate 관련 =====
const currentLang = ref("ko");
let isComboReady = false;
let pendingLang = null;
let translateObserver = null;

// 쿠키에서 googtrans 값 읽기
const getCookie = (name) => {
  if (!process.client) return "";
  const m = document.cookie.match("(^|;)\\s*" + name + "\\s*=\\s*([^;]+)");
  return m ? decodeURIComponent(m.pop()) : "";
};

// 현재 언어 업데이트
const updateCurrentLang = (langCode) => {
  if (!process.client) return;
  currentLang.value = langCode;
  // HTML 태그의 lang 속성 업데이트
  document.documentElement.setAttribute("lang", langCode);
};

// Google Translate 초기화
const googleTranslateElementInit = () => {
  if (!process.client) return;

  // google 객체가 아직 없으면 잠시 대기
  if (typeof google === "undefined" || !google.translate) {
    setTimeout(googleTranslateElementInit, 100);
    return;
  }

  new google.translate.TranslateElement(
    {
      pageLanguage: "ko",
      includedLanguages: "ko,en",
    },
    "google_translate_element"
  );

  setTimeout(waitForGoogleTranslate, 500);
};

// Google Translate 콤보 박스 대기
const waitForGoogleTranslate = (attempts = 0) => {
  if (!process.client) return;

  const combo = document.querySelector(".goog-te-combo");

  if (combo && combo.options.length > 1) {
    isComboReady = true;

    // 콤보 박스 change 이벤트 감지하여 currentLang 업데이트
    combo.removeEventListener("change", handleComboChange);
    combo.addEventListener("change", handleComboChange);

    // 로딩 중 클릭해 둔 언어가 있으면 한 번만 자동 적용
    if (pendingLang) {
      const langToApply = pendingLang;
      pendingLang = null;
      setTimeout(() => {
        triggerGoogleLanguage(langToApply);
      }, 100);
    }
  } else if (attempts < 20) {
    setTimeout(() => waitForGoogleTranslate(attempts + 1), 300);
  } else {
    // 최대 시도 횟수 초과 시에도 pendingLang이 있으면 처리
    if (pendingLang) {
      const langToApply = pendingLang;
      pendingLang = null;
      setTimeout(() => {
        waitForGoogleTranslate(0);
        setTimeout(() => {
          if (isComboReady) {
            triggerGoogleLanguage(langToApply);
          }
        }, 500);
      }, 500);
    }
  }
};

// 콤보 박스 변경 이벤트 핸들러
const handleComboChange = (e) => {
  if (!process.client) return;

  const langCode = e.target.value;

  // 값이 비어있거나(원본) 'ko'이면 한국어로 처리
  if (!langCode || langCode === "ko") {
    updateCurrentLang("ko");
  } else {
    // ko,en이 아닌 언어는 제거하고 ko로 처리
    if (langCode !== "en") {
      updateCurrentLang("ko");
    } else {
      updateCurrentLang(langCode);
    }
  }
};

// 언어 클릭 핸들러 (Vue 이벤트 핸들러용)
const handleLangClick = (e, langCode = null) => {
  e.preventDefault();
  e.stopPropagation();

  // 마운트되지 않았으면 무시
  if (!isMounted.value) {
    return;
  }

  // 언어 코드가 직접 전달되지 않으면 data-lang 속성에서 찾기
  let lang = langCode;

  if (!lang) {
    let target = e.currentTarget;

    if (!target || !target.getAttribute("data-lang")) {
      target = e.target;
    }

    if (!target || !target.getAttribute("data-lang")) {
      target = e.target.closest("[data-lang]");
    }

    lang = target?.getAttribute("data-lang");
  }

  if (!lang || (lang !== "ko" && lang !== "en")) {
    return;
  }

  // 이미 선택된 언어를 다시 클릭하면 무시
  const tr = getCookie("googtrans");
  const isCurrentlyKorean =
    !tr || tr === "" || tr === "/ko/ko" || tr.split("/").pop() === "ko";
  const isCurrentlyEnglish =
    tr && tr.includes("/en") && tr.split("/").pop() === "en";

  if (lang === "ko" && (currentLang.value === "ko" || isCurrentlyKorean)) {
    return;
  }

  if (lang === "en" && (currentLang.value === "en" || isCurrentlyEnglish)) {
    return;
  }

  // 콤보 박스가 준비되었는지 다시 확인
  const combo = document.querySelector(".goog-te-combo");

  if (!combo || !isComboReady || combo.options.length <= 1) {
    // 아직 준비되지 않았으면 대기 상태로 저장하고 재시도
    pendingLang = lang;
    if (!combo) {
      waitForGoogleTranslate();
    }
    return;
  }

  triggerGoogleLanguage(lang);

  // 비로그인용 언어 메뉴 닫기
  if (isLangMenuOpen.value) {
    closeLangMenu();
  }
};

// Google Translate 언어 변경 트리거
const triggerGoogleLanguage = (langCode) => {
  if (!process.client) return;

  const combo = document.querySelector(".goog-te-combo");

  // 콤보 박스가 없거나 준비되지 않았으면 재시도
  if (!combo || !isComboReady) {
    pendingLang = langCode;
    if (!combo) {
      waitForGoogleTranslate();
    }
    return;
  }

  combo.value = langCode;

  // change 이벤트를 두 번 발생
  const evt = new Event("change", {
    bubbles: true,
  });
  combo.dispatchEvent(evt);
  setTimeout(() => {
    combo.dispatchEvent(evt);
  }, 100);

  // 콤보 재생성 시에도 다시 바인딩되도록 초기화
  isComboReady = false;
  setTimeout(waitForGoogleTranslate, 500);
};

// MutationObserver로 콤보 박스 DOM 변경 감지
const observeComboChanges = () => {
  if (!process.client) return;

  // 기존 옵저버가 있으면 제거
  if (translateObserver) {
    translateObserver.disconnect();
    translateObserver = null;
  }

  const container = document.getElementById("google_translate_element");
  if (!container) return;

  translateObserver = new MutationObserver(() => {
    // 콤보 재생성 시 waitForGoogleTranslate 호출
    waitForGoogleTranslate();
  });

  translateObserver.observe(container, {
    childList: true,
    subtree: true,
  });
};

// ===== 더미 데이터 (잭팟 - 주석처리) =====
// const jackpotBonusData = ref([
//   { rank: 1, percentage: "25%", amount: "5,000만원" },
//   { rank: 2, percentage: "15%", amount: "3,000만원" },
//   { rank: 3, percentage: "10%", amount: "2,000만원" },
//   { rank: 4, percentage: "8%", amount: "1,600만원" },
//   { rank: 5, percentage: "7%", amount: "1,400만원" },
//   { rank: 6, percentage: "6%", amount: "1,200만원" },
//   { rank: 7, percentage: "5%", amount: "1,000만원" },
//   { rank: 8, percentage: "4%", amount: "800만원" },
//   { rank: 9, percentage: "3%", amount: "600만원" },
//   { rank: 10, percentage: "2%", amount: "400만원" },
//   {
//     rank: "11~20위",
//     percentage: "각 1.5%",
//     amount: "각 300만원 (총 3,000만원)",
//   },
// ]);

// const jackpotHistoryData = ref([
//   { rank: 1, amount: "50,000,000원", winner: "jon**" },
//   { rank: 2, amount: "30,000,000원", winner: "김**" },
//   { rank: 3, amount: "20,000,000원", winner: "tem**" },
//   { rank: 4, amount: "16,000,000원", winner: "Ave**" },
//   { rank: 5, amount: "14,000,000원", winner: "Ril**" },
//   { rank: 6, amount: "12,000,000원", winner: "Cas**" },
//   { rank: 7, amount: "10,000,000원", winner: "박**" },
//   { rank: 8, amount: "8,000,000원", winner: "이**" },
//   { rank: 9, amount: "6,000,000원", winner: "Mor**" },
//   { rank: 10, amount: "4,000,000원", winner: "sky**" },
//   { rank: "11위~20위", amount: "3,000,000원", winner: "cc**", extra: "외19명" },
// ]);

// ===== Computed =====
const formatNotificationCount = (count) => {
  if (count <= 0) return null;
  return count > NOTIFICATION_MAX_DISPLAY
    ? `+${NOTIFICATION_MAX_DISPLAY}`
    : count;
};

// const jackpotNotificationCount = computed(() =>
//   formatNotificationCount(unreadJackpotCount.value)
// );
const chatNotificationCount = computed(() =>
  formatNotificationCount(unreadChatCount.value)
);
const notificationCount = computed(() =>
  formatNotificationCount(unreadNotificationCount.value)
);

// const showJackpotNotification = computed(() => unreadJackpotCount.value > 0);
const showNotification = computed(() => unreadNotificationCount.value > 0);

// ===== Watchers =====
watch(isChatOpen, (newValue) => {
  if (process.client && newValue && unreadChatCount.value > 0) {
    try {
      unreadChatCount.value = 0;
      // 알림 확인 처리
    } catch (error) {
      console.error("채팅 알림 확인 처리 실패:", error);
    }
  }
});

// ===== 테마 관리 =====
const setTheme = (theme) => {
  let themeToSet = theme;

  // 같은 버튼을 클릭하면 반대 테마로 토글
  if (currentTheme.value === theme) {
    themeToSet = theme === "dark" ? "light" : "dark";
  }

  currentTheme.value = themeToSet;

  if (process.client) {
    document.documentElement.setAttribute("data-theme", themeToSet);
    storage.setStorage(THEME_STORAGE_KEY, themeToSet);
  }
};

const initTheme = () => {
  if (!process.client) return;

  const savedTheme = storage.getStorage(THEME_STORAGE_KEY);
  if (savedTheme && (savedTheme === "dark" || savedTheme === "light")) {
    currentTheme.value = savedTheme;
    document.documentElement.setAttribute("data-theme", savedTheme);
  } else {
    currentTheme.value = "dark";
    document.documentElement.setAttribute("data-theme", "dark");
    storage.setStorage(THEME_STORAGE_KEY, "dark");
  }
};

// ===== 알림 관리 =====
const fetchNotificationCounts = async () => {
  try {
    await fetchUnreadCount();
  } catch (error) {
    console.error("알림 개수 조회 실패:", error);
  }
};

// const markJackpotAsRead = () => {
//   if (process.client && unreadJackpotCount.value > 0) {
//     try {
//       unreadJackpotCount.value = 0;
//       // 알림 확인 처리
//     } catch (error) {
//       console.error("잭팟 알림 확인 처리 실패:", error);
//     }
//   }
// };

// ===== 잭팟 모달 관리 (주석처리) =====
// const fetchJackpotRank = async () => {
//   try {
//     jackpotRank.value = {
//       rank: 45,
//       direction: "down",
//       percentage: 4.33,
//     };
//   } catch (error) {
//     console.error("잭팟 순위 조회 실패:", error);
//   }
// };

// const closeJackpotModalIfOpen = () => {
//   if (jackpotModal.value?.isOpened()) {
//     jackpotModal.value?.close();
//   }
// };

// const openJackpotModal = async () => {
//   await fetchJackpotRank();
//   jackpotModal.value?.open();
//   markJackpotAsRead();
// };

// const openJackpotHistoryModal = () => {
//   closeJackpotModalIfOpen();
//   jackpotHistoryModal.value?.open();
// };

// const reopenJackpotModal = () => {
//   jackpotModal.value?.open();
// };

// const openJackpotBonusModal = () => {
//   closeJackpotModalIfOpen();
//   jackpotBonusModal.value?.open();
// };

// const openJackpotRankStandardModal = () => {
//   closeJackpotModalIfOpen();
//   jackpotRankStandardModal.value?.open();
// };

// ===== 스크롤 관리 =====
const handleScroll = () => {
  if (process.client) {
    // .main 요소의 스크롤 위치 확인
    const mainElement = mainElementRef.value || document.querySelector(".main");
    if (mainElement) {
      const scrollTop = mainElement.scrollTop;
      isScrolled.value = scrollTop > 0;
    } else {
      // fallback: window 스크롤 (기존 방식)
      const scrollTop =
        window.pageYOffset || document.documentElement.scrollTop;
      isScrolled.value = scrollTop > 0;
    }
  }
};

// ===== 유저 메뉴 관리 =====
const toggleUserMenu = () => {
  isUserMenuOpen.value = !isUserMenuOpen.value;
};

const closeUserMenu = () => {
  isUserMenuOpen.value = false;
};

// 언어 서브메뉴 토글
const toggleLangSubmenu = () => {
  isLangSubmenuOpen.value = !isLangSubmenuOpen.value;
};

const closeLangSubmenu = () => {
  isLangSubmenuOpen.value = false;
};

// 비로그인용 언어 메뉴 토글
const toggleLangMenu = () => {
  isLangMenuOpen.value = !isLangMenuOpen.value;
};

const closeLangMenu = () => {
  isLangMenuOpen.value = false;
};

// 사운드 토글
// const toggleSound = () => {
//   isSoundOn.value = !isSoundOn.value;
//   // localStorage에 저장
//   if (process.client) {
//     localStorage.setItem(SOUND_STORAGE_KEY, isSoundOn.value ? 'on' : 'off');
//   }
// };

// 외부 클릭 시 메뉴 닫기
const handleClickOutside = (event) => {
  if (process.client) {
    const userMenuElement = event.target.closest(".header__gnb-user-menu");
    const userButtonElement = event.target.closest(".header__gnb-user");
    const langSubmenuElement = event.target.closest(
      ".header__gnb-user-submenu"
    );
    const langButtonElement = event.target.closest(
      ".header__gnb-user-menu-link.ico-before.ico-language"
    );
    const langMenuButtonElement = event.target.closest(".header__gnb-language");

    if (isUserMenuOpen.value && !userMenuElement && !userButtonElement) {
      closeUserMenu();
    }

    // 언어 서브메뉴 외부 클릭 시 닫기
    if (isLangSubmenuOpen.value && !langSubmenuElement && !langButtonElement) {
      closeLangSubmenu();
    }

    // 비로그인용 언어 메뉴 외부 클릭 시 닫기
    if (isLangMenuOpen.value && !userMenuElement && !langMenuButtonElement) {
      closeLangMenu();
    }
  }
};

// 로그아웃
const handleLogout = () => {
  logout();
  navigateTo("/");
};

// Socket.io 연결
const { connect, on, disconnect } = useSocket();
let socketInstance = null;

// ===== Lifecycle Hooks =====
onMounted(async () => {
  if (process.client) {
    // Auth 초기화를 가장 먼저 수행
    initializeAuth();

    isMounted.value = true;

    initTheme();

    // 사운드 설정 불러오기
    // const savedSound = localStorage.getItem(SOUND_STORAGE_KEY);
    // if (savedSound) {
    //   isSoundOn.value = savedSound === 'on';
    // }

    await fetchNotificationCounts();

    // Socket.io 연결 및 잔액 업데이트 구독
    if (isLoggedIn.value && currentUser.value) {
      socketInstance = connect(
        currentUser.value.id,
        currentUser.value.nickname,
        currentUser.value.avatar
      );

      // 잔액 업데이트 이벤트 구독
      on('balance:update', (data) => {
        // // console.log('[Header] Balance updated:', data.balance);
        if (data.balance !== undefined) {
          // useAuth의 updateBalance 사용
          updateBalance(Number(data.balance));
        }
      });
    }

    // .main 요소 찾기
    await nextTick();
    const mainElement = document.querySelector(".main");
    if (mainElement) {
      mainElementRef.value = mainElement;
      // 초기 스크롤 위치 확인
      handleScroll();
      // .main 요소의 스크롤 이벤트 리스너 등록
      mainElement.addEventListener("scroll", handleScroll, { passive: true });
    } else {
      // fallback: window 스크롤 (기존 방식)
      handleScroll();
      window.addEventListener("scroll", handleScroll, { passive: true });
    }

    // 외부 클릭 이벤트 리스너 등록
    document.addEventListener("click", handleClickOutside);

    // Google Translate 요소 동적 생성 (hydration mismatch 방지)
    const translateElement = document.getElementById(
      "google_translate_element"
    );
    if (!translateElement) {
      const div = document.createElement("div");
      div.id = "google_translate_element";
      document.body.appendChild(div);
    }

    // Google Translate 초기화
    await nextTick();

    window.googleTranslateElementInit = googleTranslateElementInit;

    // 이미 로드되어 있다면 바로 초기화
    if (typeof google !== "undefined" && google.translate) {
      googleTranslateElementInit();
    }

    // MutationObserver 시작
    observeComboChanges();

    // 페이지 로드 시 현재 언어 초기화
    const tr = getCookie("googtrans");
    const parts = tr.split("/");
    let currentLangFromCookie = parts.pop() || "ko";
    // ko,en이 아닌 언어는 ko로 처리
    if (currentLangFromCookie !== "ko" && currentLangFromCookie !== "en") {
      currentLangFromCookie = "ko";
    }
    updateCurrentLang(currentLangFromCookie);
  }
});

onUnmounted(() => {
  if (process.client) {
    // .main 요소의 스크롤 이벤트 리스너 제거
    if (mainElementRef.value) {
      mainElementRef.value.removeEventListener("scroll", handleScroll);
    } else {
      // fallback: window 스크롤 리스너 제거
      window.removeEventListener("scroll", handleScroll);
    }
    document.removeEventListener("click", handleClickOutside);

    // MutationObserver 정리
    if (translateObserver) {
      translateObserver.disconnect();
      translateObserver = null;
    }

    // Vue의 @click 핸들러를 사용하므로 DOM 이벤트 리스너 제거 불필요
    // (Vue가 자동으로 정리함)

    // 콤보 박스 이벤트 리스너 제거
    const combo = document.querySelector(".goog-te-combo");
    if (combo) {
      combo.removeEventListener("change", handleComboChange);
    }

    // 전역 콜백 함수 제거
    if (window.googleTranslateElementInit) {
      delete window.googleTranslateElementInit;
    }
  }
});
</script>

<template>
  <header class="header" :class="{ 'header--active': isScrolled }">
    <!-- 로고 -->
    <h1 class="header__logo">
      <NuxtLink to="/">
        <span class="blind">BITUP</span>
      </NuxtLink>
    </h1>
    <!-- GNB -->
    <nav class="header__gnb">
      <ul class="header__gnb-list">
        <!-- isMounted && isAuthInitialized 체크 추가 -->
        <template v-if="isMounted && isAuthInitialized">
          <template v-if="!isLoggedIn">
            <li class="header__gnb-item">
              <button type="button" class="header__gnb-link btn-gray btn-sm" @click="openLoginModal">
                로그인
              </button>
            </li>
            <li class="header__gnb-item">
              <button type="button" class="header__gnb-link btn-primary btn-sm" @click="openSignupModal">가입</button>
            </li>
          </template>
          <li v-if="isLoggedIn" class="header__gnb-item">
            <!-- 크레딧 -->
            <div class="header__gnb-credit">
              <div class="header__gnb-credit-info">
                <i class="ico-wallet"></i>
                <span>₩ {{ Number(currentUser?.balance || 0).toLocaleString('ko-KR') }}</span>
              </div>
              <button type="button" class="btn-primary btn-sm">충전</button>
            </div>
          </li>
          <template v-if="!isMobile">
            <!-- 잭팟 보너스 (주석처리) -->
            <!-- <li v-if="isLoggedIn" class="header__gnb-item">
              <button type="button" class="btn-ghost btn-sm ico-before ico-jackpot" @click="openJackpotModal">
                <span class="blind">잭팟 보너스</span>
                <i v-if="showJackpotNotification" class="notification" :class="{ 'notification-overflow': unreadJackpotCount > 99 }">
                  {{ jackpotNotificationCount }}
                </i>
              </button>
            </li> -->
            <li class="header__gnb-item">
              <!-- 채팅 -->
              <button type="button" class="btn-ghost btn-sm ico-before ico-chat" :class="{ active: isChatOpen }" @click="toggleChat">
                <span class="blind">채팅</span>
                <i v-if="chatNotificationCount" class="notification" :class="{ 'notification-overflow': unreadChatCount > 99 }">
                  {{ chatNotificationCount }}
                </i>
              </button>
            </li>
            <li v-if="isLoggedIn" class="header__gnb-item">
              <!-- 알림 -->
              <button type="button" class="btn-ghost btn-sm ico-before ico-bell" :class="{ active: isNotificationOpen }" @click="toggleNotification">
                <span class="blind">알림</span>
                <i v-if="showNotification" class="notification" :class="{
                  'notification-overflow': unreadNotificationCount > 99,
                }">
                  {{ notificationCount }}
                </i>
              </button>
            </li>
            <!-- <li class="header__gnb-item">
              사운드
              <button type="button" class="header__gnb-sound btn-ghost btn-sm ico-before" :class="{
                'ico-sound-on': isSoundOn,
                'ico-sound-off': !isSoundOn,
              }" @click="toggleSound">
                {{ isSoundOn ? "ON" : "OFF" }}
              </button>
            </li> -->
            <li v-if="!isLoggedIn" class="header__gnb-item">
              <!-- 언어 -->
              <button type="button" class="header__gnb-language btn-ghost btn-sm ico-before ico-language" :class="{ active: isLangMenuOpen }" @click="toggleLangMenu">
                <span class="blind">언어</span>
              </button>

              <AnimatePresence>
                <motion.div v-if="isLangMenuOpen" key="lang-menu" class="header__gnb-user-menu" :initial="{
                  opacity: 0,
                  y: -10,
                }" :animate="{
                  opacity: 1,
                  y: 0,
                  transition: {
                    type: 'spring',
                    stiffness: 300,
                    damping: 20,
                    mass: 0.5,
                  },
                }" :exit="{
                  opacity: 0,
                  y: -10,
                  transition: {
                    duration: 0.15,
                    ease: [0.4, 0, 0.2, 1],
                  },
                }">
                  <ul class="header__gnb-user-menu-list">
                    <li class="header__gnb-user-menu-item">
                      <button type="button" class="header__gnb-user-submenu-link" :class="{ active: isMounted && currentLang === 'ko' }" data-lang="ko" @click="(e) => handleLangClick(e, 'ko')">
                        한국어
                      </button>
                    </li>
                    <li class="header__gnb-user-menu-item">
                      <button type="button" class="header__gnb-user-submenu-link" :class="{ active: isMounted && currentLang === 'en' }" data-lang="en" @click="(e) => handleLangClick(e, 'en')">
                        English(영어)
                      </button>
                    </li>
                  </ul>
                </motion.div>
              </AnimatePresence>
            </li>
            <li class="header__gnb-item">
              <!-- 테마 -->
              <div class="header__gnb-theme btn-ghost btn-sm">
                <button type="button" class="btn-dark ico-before ico-moon" :class="{ active: currentTheme === 'dark' }" @click="setTheme('dark')">
                  <span class="blind">다크 모드</span>
                </button>
                <button type="button" class="btn-light ico-before ico-sun" :class="{ active: currentTheme === 'light' }" @click="setTheme('light')">
                  <span class="blind">라이트 모드</span>
                </button>
              </div>
            </li>
          </template>
          <li v-if="isLoggedIn" class="header__gnb-item">
            <!-- 유저 -->
            <button type="button" class="header__gnb-user" @click="toggleUserMenu">
              <img :src="avatarUrl" :alt="currentUser?.nickname || 'user'" @error="handleAvatarError" />
            </button>
            <AnimatePresence>
              <motion.div v-if="isUserMenuOpen" key="user-menu" class="header__gnb-user-menu" :initial="{
                opacity: 0,
                y: -10,
              }" :animate="{
                opacity: 1,
                y: 0,
                transition: {
                  type: 'spring',
                  stiffness: 300,
                  damping: 20,
                  mass: 0.5,
                },
              }" :exit="{
                opacity: 0,
                y: -10,
                transition: {
                  duration: 0.15,
                  ease: [0.4, 0, 0.2, 1],
                },
              }">
                <ul class="header__gnb-user-menu-list">
                  <li class="header__gnb-user-menu-item">
                    <NuxtLink to="/mypage" class="header__gnb-user-menu-link ico-before ico-user-thin">프로필</NuxtLink>
                  </li>
                  <li class="header__gnb-user-menu-item">
                    <NuxtLink to="/mypage/billing" class="header__gnb-user-menu-link ico-before ico-prefix">입/출금</NuxtLink>
                  </li>
                  <li class="header__gnb-user-menu-item">
                    <NuxtLink to="/mypage/history" class="header__gnb-user-menu-link ico-before ico-history">배팅내역</NuxtLink>
                  </li>
                  <li class="header__gnb-user-menu-item">
                    <button type="button" class="header__gnb-user-menu-link ico-before ico-language" :class="{ active: isLangSubmenuOpen }" @click="toggleLangSubmenu">
                      언어변경
                      <i class="ico-chevron-down"></i>
                    </button>
                    <AnimatePresence>
                      <motion.ul v-if="isLangSubmenuOpen" key="lang-submenu" class="header__gnb-user-submenu" :initial="{
                        opacity: 0,
                        y: -10,
                      }" :animate="{
                        opacity: 1,
                        y: 0,
                        transition: {
                          type: 'spring',
                          stiffness: 300,
                          damping: 20,
                          mass: 0.5,
                        },
                      }" :exit="{
                        opacity: 0,
                        y: -10,
                        transition: {
                          duration: 0.15,
                          ease: [0.4, 0, 0.2, 1],
                        },
                      }">
                  <li class="header__gnb-user-submenu-item">
                    <button type="button" class="header__gnb-user-submenu-link" :class="{ active: isMounted && currentLang === 'ko' }" data-lang="ko" @click="(e) => handleLangClick(e, 'ko')">
                      한국어
                    </button>
                  </li>
                  <li class="header__gnb-user-submenu-item">
                    <button type="button" class="header__gnb-user-submenu-link" :class="{ active: isMounted && currentLang === 'en' }" data-lang="en" @click="(e) => handleLangClick(e, 'en')">
                      English(영어)
                    </button>
                  </li>
                  </motion.ul>
            </AnimatePresence>
          </li>
          <li class="header__gnb-user-menu-item">
            <button type="button" class="header__gnb-user-menu-link header__gnb-user-menu-link--logout ico-before ico-logout" @click="handleLogout">
              로그아웃
            </button>
          </li>
      </ul>
      </motion.div>
      </AnimatePresence>
      </li>
</template>
</ul>
</nav>
</header>

<!-- 모바일 GNB -->
<nav v-if="isMobile" class="header__mobile-gnb">
  <ul class="header__mobile-gnb-list">
    <li class="header__mobile-gnb-item">
      <!-- 홈 -->
      <NuxtLink to="/" class="header__mobile-gnb-btn header__mobile-gnb-btn--home ico-before ico-home-m">
        홈
      </NuxtLink>
    </li>
    <!-- 잭팟 (주석처리) -->
    <!-- <li class="header__mobile-gnb-item">
      <button type="button" class="header__mobile-gnb-btn header__mobile-gnb-btn--jackpot ico-before ico-jackpot-m" @click="openJackpotModal">
        <span>잭팟</span>
        <i v-if="showJackpotNotification" class="notification" :class="{ 'notification-overflow': unreadJackpotCount > 99 }">
          {{ jackpotNotificationCount }}
        </i>
      </button>
    </li> -->
    <li class="header__mobile-gnb-item">
      <!-- 채팅 -->
      <button type="button" class="header__mobile-gnb-btn header__mobile-gnb-btn--chat ico-before ico-chat-m" :class="{ active: isChatOpen }" @click="toggleChat">
        <span>채팅</span>
        <i v-if="chatNotificationCount" class="notification" :class="{ 'notification-overflow': unreadChatCount > 99 }">
          {{ chatNotificationCount }}
        </i>
      </button>
    </li>
    <li class="header__mobile-gnb-item">
      <!-- 알림 -->
      <button type="button" class="header__mobile-gnb-btn header__mobile-gnb-btn--bell ico-before ico-bell-m" :class="{ active: isNotificationOpen }" @click="toggleNotification">
        <span>알림</span>
        <i v-if="notificationCount" class="notification" :class="{
          'notification-overflow': unreadNotificationCount > 99,
        }">
          {{ notificationCount }}
        </i>
      </button>
    </li>
  </ul>
  <i class="header__mobile-gnb-bar"></i>
</nav>

<!-- 잭팟 보너스 모달 (주석처리) -->
<!-- <Modal ref="jackpotModal" title="잭팟 보너스 (Friday Jackpot)" type="a">
  <template #default="{ close }">
    <div class="modal__content-top">
      <p>
        매주 금요일, 주간 손실
        <strong class="point-yellow">TOP 20에게 총 2억원</strong>을
        지급합니다.
      </p>
    </div>
    <div class="modal__content-body">
      <div class="modal__jackpot">
        <div class="modal__jackpot-header">
          <p class="modal__jackpot-title">
            <strong>Jonston님</strong>
            <span>의 잭팟 당첨 순위</span>
            <button type="button" class="modal__jackpot-question ico-question" @click="openJackpotRankStandardModal">
              <span class="blind">잭팟 당첨 순위 기준</span>
            </button>
          </p>
        </div>
        <div class="modal__jackpot-body">
          <div class="modal__jackpot-rank" :class="`modal__jackpot-rank--${jackpotRank.direction}`">
            <strong>{{ jackpotRank.rank }}위</strong>
            <span class="badge badge-chart ico-before" :class="[
              `badge--${jackpotRank.direction}`,
              `ico-${jackpotRank.direction}`,
            ]"></span>
            <span class="badge" :class="`badge--${jackpotRank.direction}`">
              {{
                jackpotRank.percentage > 0
                  ? `+${jackpotRank.percentage}%`
                  : `${jackpotRank.percentage}%`
              }}
            </span>
          </div>
        </div>
      </div>
    </div>
    <div class="modal__content-footer modal__content-footer--start">
      <button type="button" class="btn-primary" @click="openJackpotHistoryModal">
        잭팟 지급내역
      </button>
      <button type="button" class="btn-gray" @click="openJackpotBonusModal">
        보너스 지급표
      </button>
    </div>
    <div class="modal__jackpot-thumb">
      <img src="@/assets/images/jackpot.png" alt="잭팟 썸네일" />
    </div>
  </template>
</Modal> -->

<!-- 잭팟 지급 내역 모달 (주석처리) -->
<!-- <Modal ref="jackpotHistoryModal" title="잭팟 지급 내역" type="a" :previous-modal="reopenJackpotModal">
  <template #default="{ close }">
    <div class="modal__content-top">
      <p>
        {{ jackpotHistoryMeta.round }}회차 결과 :
        {{ jackpotHistoryMeta.startDate }}
        <strong class="point-primary">
          {{ jackpotHistoryMeta.startTime }} ~
          {{ jackpotHistoryMeta.endDate }}</strong>
      </p>
    </div>
    <div class="modal__content-body">
      <div class="modal__jackpot">
        <div class="modal__jackpot-table-wrap">
          <table class="modal__jackpot-table">
            <caption class="modal__jackpot-table-caption">
              <p>
                <strong class="point-primary">{{ jackpotHistoryMeta.round }}회차</strong>
                당첨자
              </p>
            </caption>
            <colgroup>
              <col width="90px" />
              <col />
              <col width="120px" />
            </colgroup>
            <thead>
              <tr>
                <th class="modal__jackpot-table-th">순위</th>
                <th class="modal__jackpot-table-th">등위별 당첨금액</th>
                <th class="modal__jackpot-table-th">당첨자</th>
              </tr>
            </thead>
            <tbody>
              <tr v-for="(item, index) in jackpotHistoryData" :key="index">
                <td class="modal__jackpot-table-td" :class="{ 'modal__jackpot-table-td--primary': index < 3 }">
                  {{
                    typeof item.rank === "number"
                      ? `${item.rank}위`
                      : item.rank
                  }}
                </td>
                <td class="modal__jackpot-table-td" :class="{ 'modal__jackpot-table-td--primary': index < 3 }">
                  {{ item.amount }}
                </td>
                <td class="modal__jackpot-table-td">
                  {{ item.winner }}
                  <span v-if="item.extra">{{ item.extra }}</span>
                </td>
              </tr>
            </tbody>
          </table>
        </div>
      </div>
    </div>
  </template>
</Modal> -->

<!-- Friday Jacpot 보너스 지급표 모달 (주석처리) -->
<!-- <Modal ref="jackpotBonusModal" title="Friday Jacpot 보너스 지급표" type="a">
  <template #default="{ close }">
    <div class="modal__content-top">
      <p>
        매주 금요일, 주간 손실
        <strong class="point-yellow">TOP 20에게 총 2억원</strong>을 아래와
        같이 지급하며, <br v-if="!isMobile" />
        보너스 당청금은 테더(USDT)로 입금합니다.
      </p>
    </div>
    <div class="modal__content-body">
      <div class="modal__jackpot">
        <ul class="modal__jackpot-bonus-list">
          <li v-for="(item, index) in jackpotBonusData" :key="index" class="modal__jackpot-bonus-item">
            <span :class="{ 'modal__jackpot-bonus-item--primary': index < 3 }">
              {{
                typeof item.rank === "number" ? `${item.rank}위` : item.rank
              }}
              {{ item.percentage }} = {{ item.amount }}
            </span>
          </li>
        </ul>
        <p class="modal__jackpot-bonus-note">
          지급 금액은 운영 정책에 따라 변동될 수 있으며, <br />
          최종 지급 내역은 <strong>"내 보너스 내역"</strong>에서 확인
          가능합니다.
        </p>
      </div>
    </div>
    <div class="modal__content-footer modal__content-footer--end">
      <button type="button" class="btn-gray" @click="close">닫기</button>
      <NuxtLink to="/mypage/wallet" class="btn-primary">내 보너스 내역</NuxtLink>
    </div>
  </template>
</Modal> -->

<!-- 잭팟 당첨 순위 기준 모달 (주석처리) -->
<!-- <Modal ref="jackpotRankStandardModal" title="잭팟 당첨 순위 기준" type="a" :previous-modal="reopenJackpotModal">
  <template #default="{ close }">
    <div class="modal__content-top">
      <p>
        집계 기간 : 매주
        <strong class="point-primary">금요일 00:00:00 ~ 목요일 23:59:59</strong>
        (KST)
      </p>
      <p>
        발표/지급 : 매주
        <strong class="point-primary">금요일 12:00</strong> 자동 지급
      </p>
    </div>
    <div class="modal__content-body">
      <div class="modal__jackpot">
        <div class="modal__jackpot-alert">
          <strong class="modal__jackpot-alert-title">산정 방식(주간 순손실)</strong>
          <div class="modal__jackpot-alert-desc">
            <p>주간 순손실 = 주간 총 베팅금 - 주간 당첨금(수익)</p>
            <p>
              집계 대상 : 프로모션/무료지급/보너스성 크레딧, 운영정책
              위반/어뷰징 계정
            </p>
          </div>
        </div>

        <div class="modal__jackpot-alert">
          <strong class="modal__jackpot-alert-title">동점 처리</strong>
          <div class="modal__jackpot-alert-desc">
            <p>1순위 : 주간 순손실이 큰 유저</p>
            <p>동점 시 : 총 베팅금이 큰 유저 우선</p>
            <p>
              그래도 동점 시 : 먼저 해당 손실을 기록한 유저 우선, 또는 공동
              순위 처리
            </p>
          </div>
        </div>
      </div>
    </div>
  </template>
</Modal> -->

<Modal ref="loginModal" title="로그인" type="c" @close="handleLoginModalClose">
  <template #default="{ close }">
    <div class="modal__content-body modal__content-body--login">
      <div class="auth__wrap">
        <h1 class="auth__logo">
          <span class="blind">BITUP</span>
        </h1>

        <!-- 로그인 폼 화면 -->
        <div class="login__form-wrapper">
          <div class="login__form">
            <div class="login__form-item">
              <label for="login-email" class="login__form-label">Account</label>
              <input ref="email.inputRef" type="text" class="input-text input-lg" :class="{
                'input-error': email.isError.value,
                'input-shake': email.shakeKey.value > 0,
              }" :key="`email-${email.shakeKey.value}`" id="login-email" placeholder="계정을 입력해주세요." v-model="email.value.value" @keyup.enter="handleLogin" />
              <Transition name="error">
                <span v-if="email.isError.value" class="login__form-error">
                  {{ email.errorMessage.value }}
                </span>
              </Transition>
            </div>

            <div class="login__form-item">
              <label for="login-password" class="login__form-label">Password</label>
              <div class="login__eye-wrap">
                <input ref="password.inputRef" :type="showPassword ? 'text' : 'password'" class="input-text input-lg input-pw" :class="{
                  'input-error': password.isError.value,
                  'input-shake': password.shakeKey.value > 0,
                }" :key="`password-${password.shakeKey.value}`" id="login-password" placeholder="비밀번호를 입력해주세요." v-model="password.value.value" @keyup.enter="handleLogin" />
                <button v-show="password.value.value.length > 0" type="button" class="login__eye" :class="showPassword ? 'ico-eye-active' : 'ico-eye'
                  " @click="togglePassword">
                  <span class="blind">비밀번호 보기</span>
                </button>
              </div>
              <Transition name="error">
                <span v-if="password.isError.value" class="login__form-error">
                  {{ password.errorMessage.value }}
                </span>
              </Transition>
            </div>

            <div>
              <button type="button" class="login__form-link" @click="openPasswordRequestModal">비밀번호를 잊어버리셨나요?</button>
            </div>

            <div class="login__form-btn">
              <button type="button" class="btn-primary btn-block btn-md" @click="handleLogin">
                로그인
              </button>
              <div class="login__form-text">
                <span>BITUP이 처음이신가요?</span>
                <button type="button" class="login__form-text-link" @click="openSignupModal">계정 만들기</button>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  </template>
</Modal>

<Modal ref="signupModal" title="계정만들기" type="c" @close="handleSignupModalClose">
  <template #default="{ close }">
    <div class="modal__content-body modal__content-body--login">
      <div class="login__form">
        <!-- 이메일 주소 -->
        <div class="login__form-item">
          <label for="signup-email" class="login__form-label">이메일 주소</label>
          <div class="login__form-item-wrap">
            <input ref="signupEmail.inputRef" type="email" class="input-text input-lg" :class="{
              'input-error': signupEmail.isError.value,
              'input-shake': signupEmail.shakeKey.value > 0,
            }" :key="`email-${signupEmail.shakeKey.value}`" id="signup-email" placeholder="이메일주소를 입력하세요." v-model="signupEmail.value.value" @keyup.enter="handleSignup" />
            <button type="button" class="btn-primary btn-md" @click="handleEmailAuth">
              인증
            </button>
          </div>
          <Transition name="error">
            <span v-if="signupEmail.isError.value" class="login__form-error">
              {{ signupEmail.errorMessage.value }}
            </span>
          </Transition>
        </div>
        <!-- 닉네임 -->
        <div class="login__form-item">
          <label for="signup-nickname" class="login__form-label">닉네임</label>
          <input ref="signupNickname.inputRef" type="text" class="input-text input-lg" :class="{
            'input-error': signupNickname.isError.value,
            'input-shake': signupNickname.shakeKey.value > 0,
          }" :key="`nickname-${signupNickname.shakeKey.value}`" id="signup-nickname" placeholder="닉네임을 입력하세요." v-model="signupNickname.value.value" @keyup.enter="handleSignup" />
          <Transition name="error">
            <span v-if="signupNickname.isError.value" class="login__form-error">
              {{ signupNickname.errorMessage.value }}
            </span>
          </Transition>
        </div>
        <!-- 비밀번호 -->
        <div class="login__form-item">
          <label for="signup-password" class="login__form-label">비밀번호</label>
          <div class="login__eye-wrap">
            <input ref="signupPassword.inputRef" :type="signupPassword.showPassword.value ? 'text' : 'password'" class="input-text input-lg input-pw" :class="{
              'input-error': signupPassword.isError.value,
              'input-shake': signupPassword.shakeKey.value > 0,
            }" :key="`password-${signupPassword.shakeKey.value}`" id="signup-password" placeholder="비밀번호를 입력해주세요." v-model="signupPassword.value.value" @keyup.enter="handleSignup" />
            <button v-show="signupPassword.value.value.length > 0" type="button" class="login__eye" :class="signupPassword.showPassword.value ? 'ico-eye-active' : 'ico-eye'" @click="signupPassword.togglePassword">
              <span class="blind">비밀번호 보기</span>
            </button>
          </div>
          <Transition name="error">
            <span v-if="signupPassword.isError.value" class="login__form-error">
              {{ signupPassword.errorMessage.value }}
            </span>
          </Transition>
        </div>
        <!-- 비밀번호 확인 -->
        <div class="login__form-item">
          <label for="signup-password-confirm" class="login__form-label">
            비밀번호 확인
          </label>
          <div class="login__eye-wrap">
            <input ref="signupPasswordConfirm.inputRef" :type="signupPasswordConfirm.showPassword.value ? 'text' : 'password'" class="input-text input-lg input-pw" :class="{
              'input-error': signupPasswordConfirm.isError.value,
              'input-shake': signupPasswordConfirm.shakeKey.value > 0,
            }" :key="`password-confirm-${signupPasswordConfirm.shakeKey.value}`" id="signup-password-confirm" placeholder="비밀번호를 입력해주세요." v-model="signupPasswordConfirm.value.value" @keyup.enter="handleSignup" />
            <button v-show="signupPasswordConfirm.value.value.length > 0" type="button" class="login__eye" :class="signupPasswordConfirm.showPassword.value ? 'ico-eye-active' : 'ico-eye'
              " @click="signupPasswordConfirm.togglePassword">
              <span class="blind">비밀번호 보기</span>
            </button>
          </div>
          <Transition name="error">
            <span v-if="signupPasswordConfirm.isError.value" class="login__form-error">
              {{ signupPasswordConfirm.errorMessage.value }}
            </span>
          </Transition>
        </div>
        <!-- 추천인 코드 -->
        <div class="login__form-item">
          <label for="signup-referral-code" class="login__form-label">
            추천인 코드
          </label>
          <input ref="signupReferralCode.inputRef" type="text" class="input-text input-lg" :class="{
            'input-error': signupReferralCode.isError.value,
            'input-shake': signupReferralCode.shakeKey.value > 0,
          }" :key="`referral-code-${signupReferralCode.shakeKey.value}`" id="signup-referral-code" placeholder="추천인 코드를 입력하세요." v-model="signupReferralCode.value.value" @keyup.enter="handleSignup" />
          <Transition name="error">
            <span v-if="signupReferralCode.isError.value" class="login__form-error">
              {{ signupReferralCode.errorMessage.value }}
            </span>
          </Transition>
        </div>
        <!-- 사용자 계약 동의 -->
        <div class="login__form-agree">
          <div class="checkset checkset--fill checkset--lg">
            <input id="signup-notification-system-unread" v-model="agreeToTerms" class="checkset__input" type="checkbox" />
            <label for="signup-notification-system-unread" class="checkset__label">
              <strong>사용자 계약</strong>에 동의하고 18세 이상임을 확인합니다.
            </label>
          </div>
        </div>
        <!-- 가입 버튼 -->
        <div class="login__form-btn">
          <button type="button" class="btn-primary btn-block btn-md" :disabled="!isSignupFormValid" @click="handleSignup">
            가입
          </button>
        </div>
      </div>
    </div>
  </template>
</Modal>

<!-- 비밀번호 찾기 모달 (로그인 모달에서 열리므로 닫을 때 로그인 모달 다시 열기) -->
<Modal ref="passwordRequestModal" title="비밀번호 찾기" type="c" :previous-modal="reopenLoginModal" @close="handlePasswordRequestModalClose">
  <template #default="{ close }">
    <div class="modal__content-body modal__content-body--login">
      <div class="auth__wrap">
        <h1 class="auth__logo">
          <span class="blind">BITUP</span>
        </h1>

        <!-- 비밀번호 찾기 폼 화면 -->
        <div v-if="!isPasswordRequestSubmitted" class="login__form-wrapper">
          <p class="auth__desc" style="margin-bottom: 1rem;">
            가입할 때 사용한 이메일 주소를 입력하면<br />
            비밀번호 재설정 방법을 보내드립니다.
          </p>
          <div class="login__form">
            <div class="login__form-item">
              <label for="request-email" class="login__form-label">이메일 주소</label>
              <input ref="requestPasswordEmail.inputRef" type="email" class="input-text input-lg" :class="{
                'input-error': requestPasswordEmail.isError.value,
                'input-shake': requestPasswordEmail.shakeKey.value > 0,
              }" :key="`request-email-${requestPasswordEmail.shakeKey.value}`" id="request-email" placeholder="이메일주소를 입력하세요." v-model="requestPasswordEmail.value.value" @keyup.enter="handlePasswordRequest" />
              <Transition name="error">
                <span v-if="requestPasswordEmail.isError.value" class="login__form-error">
                  {{ requestPasswordEmail.errorMessage.value }}
                </span>
              </Transition>
            </div>
            <div class="login__form-btn">
              <button type="button" class="btn-primary btn-block btn-md" :disabled="!isPasswordRequestFormValid" @click="handlePasswordRequest">
                비밀번호 재설정
              </button>
            </div>
          </div>
        </div>

        <!-- 비밀번호 재설정 요청 완료 화면 -->
        <div v-else class="login__form-wrapper">
          <p class="auth__desc" style="margin-bottom: 1rem;">
            가입된 계정 이메일로 비밀번호를 재설정하는 방법을 보내드렸습니다.<br /><br />
            아래 이메일로 발송되었습니다.<br />
            <strong>{{ requestPasswordEmail.value.value }}</strong>
          </p>
          <div class="login__form">
            <div class="login__form-btn">
              <button type="button" class="btn-primary btn-block btn-md" @click="handlePasswordRequestDoneLogin">
                로그인
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  </template>
</Modal>
</template>

<style scoped></style>
